import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { isAuthenticated, getCurrentUser } from '../login/controller/login-controller';
import spinService from '../services/spinService';
import { remiseService } from '../services/remiseService';

const TOTAL_SEGMENTS = 7;
const LOSE_COLORS = ['#E8E8E8', '#F0F0F0'];

// Always returns exactly 7 segments: win remises distributed evenly, rest are "Revenez demain"
function buildSegments(remises) {
  const activeRemises = (remises || []).slice(0, TOTAL_SEGMENTS - 1); // max 6 wins
  const n = activeRemises.length;

  if (n === 0) {
    return Array.from({ length: TOTAL_SEGMENTS }, (_, i) => ({
      label: 'Revenez\ndemain',
      color: LOSE_COLORS[i % 2],
      textColor: '#666',
    }));
  }

  const loseCount = TOTAL_SEGMENTS - n;
  const result = [];
  let losePlaced = 0;

  for (let i = 0; i < n; i++) {
    const r = activeRemises[i];
    result.push({
      label: r.label || `${r.pourcentage}%`,
      color: r.color || '#6BCB77',
      textColor: r.textColor || '#fff',
      pourcentage: r.pourcentage,
    });
    // Distribute lose segments evenly after each win using Bresenham-style
    const losesAfter = Math.round(((i + 1) * loseCount) / n) - losePlaced;
    for (let j = 0; j < losesAfter; j++) {
      result.push({
        label: 'Revenez\ndemain',
        color: LOSE_COLORS[(losePlaced + j) % 2],
        textColor: '#666',
      });
    }
    losePlaced += losesAfter;
  }

  return result;
}

const RouletteWheel = ({ isOpen, onClose, onNeedLogin }) => {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [canSpin, setCanSpin] = useState(true);
  const [segments, setSegments] = useState(() => buildSegments([]));
  const canvasRef = useRef(null);

  // Fetch active remises at mount and each time the modal opens
  useEffect(() => {
    remiseService.getAllActive()
      .then((data) => setSegments(buildSegments(Array.isArray(data) ? data : [])))
      .catch(() => setSegments(buildSegments([])));
  }, [isOpen]);

  const checkCanSpin = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      const data = await spinService.canSpin();
      setCanSpin(data.canSpin);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      checkCanSpin();
      setShowResult(false);
      setResult(null);
    }
  }, [isOpen, checkCanSpin]);

  const drawWheel = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = center - 6;
    const textRadius = radius * 0.62;
    const segAngle = 360 / segments.length;

    ctx.clearRect(0, 0, size, size);

    // Outer ring shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(center, center, radius + 4, 0, Math.PI * 2);
    ctx.strokeStyle = '#174540';
    ctx.lineWidth = 6;
    ctx.stroke();
    ctx.restore();

    // Draw segments
    segments.forEach((seg, i) => {
      const startAngle = (i * segAngle - 90) * (Math.PI / 180);
      const endAngle = ((i + 1) * segAngle - 90) * (Math.PI / 180);
      const midAngle = (startAngle + endAngle) / 2;

      // Fill segment
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.arc(center, center, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = seg.color;
      ctx.fill();

      // Segment border
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // --- Text rendering ---
      const lines = seg.label.split('\n');
      const isLose = seg.label.includes('Revenez');
      const fontSize = isLose ? 10 : 13;
      const lineH = fontSize + 3;
      const totalH = lines.length * lineH;

      // Detect if segment faces the left half → text would be upside-down without correction
      const midDeg = (((midAngle * 180) / Math.PI) % 360 + 360) % 360; // 0–360
      const isFlipped = midDeg > 90 && midDeg < 270;

      ctx.save();
      ctx.translate(center, center);
      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 3;
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.fillStyle = seg.textColor;

      if (isFlipped) {
        // Rotate 180° extra so text reads correctly for left-half segments
        ctx.rotate(midAngle + Math.PI);
        ctx.textAlign = 'center';
        lines.forEach((line, idx) => {
          const yOffset = -totalH / 2 + lineH * idx + lineH / 2;
          ctx.fillText(line, -textRadius, yOffset);
        });
      } else {
        ctx.rotate(midAngle);
        ctx.textAlign = 'center';
        lines.forEach((line, idx) => {
          const yOffset = -totalH / 2 + lineH * idx + lineH / 2;
          ctx.fillText(line, textRadius, yOffset);
        });
      }
      ctx.restore();
    });

    // Center circle
    ctx.beginPath();
    ctx.arc(center, center, 26, 0, Math.PI * 2);
    const grad = ctx.createRadialGradient(center - 6, center - 6, 2, center, center, 26);
    grad.addColorStop(0, '#2a7a6f');
    grad.addColorStop(1, '#174540');
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // Center emoji
    ctx.font = '18px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🎰', center, center);
  }, [segments]);

  // Redraw when modal opens or segments change
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(drawWheel, 50);
      return () => clearTimeout(t);
    }
  }, [isOpen, drawWheel, segments]);

  const handleSpin = async () => {
    if (spinning) return;

    if (!isAuthenticated()) {
      onNeedLogin();
      return;
    }

    const user = getCurrentUser();
    if (user?.type !== 'Client') {
      setResult({ hasWon: false, message: 'La roulette est réservée aux clients.' });
      setShowResult(true);
      return;
    }

    if (!canSpin) {
      setResult({ hasWon: false, message: 'Vous avez déjà tourné la roue aujourd\'hui. Revenez demain !' });
      setShowResult(true);
      return;
    }

    setSpinning(true);
    setShowResult(false);

    try {
      const data = await spinService.play();

      // Calculate target segment index based on result
      const segAngle = 360 / segments.length;
      let targetIndex = -1;

      if (data.hasWon) {
        // Find the win segment matching the returned pourcentage
        targetIndex = segments.findIndex(
          (s) => s.pourcentage !== undefined && String(s.pourcentage) === String(data.result)
        );
      }

      if (targetIndex === -1) {
        // Find an actual lose segment (no pourcentage property)
        targetIndex = segments.findIndex((s) => s.pourcentage === undefined);
        if (targetIndex === -1) targetIndex = 0; // last resort
      }

      // Calculate rotation ensuring the pointer lands on the correct segment
      // regardless of accumulated rotation state
      const targetAngle = 360 - (targetIndex * segAngle + segAngle / 2);
      const fullSpins = 5 + Math.floor(Math.random() * 3);
      const currentMod = rotation % 360;
      const needed = ((targetAngle - currentMod) + 360) % 360;
      const newRotation = rotation + fullSpins * 360 + (needed || 360);

      setRotation(newRotation);
      setResult(data);
      setCanSpin(false);

      // Show result after spin animation (4 seconds)
      setTimeout(() => {
        setSpinning(false);
        setShowResult(true);
      }, 4000);
    } catch (error) {
      setSpinning(false);
      setResult({ hasWon: false, message: "Erreur lors du tirage. Veuillez réessayer." });
      setShowResult(true);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget && !spinning) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.7, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-3xl shadow-2xl p-6 sm:p-8 max-w-md w-full mx-4"
        >
          {/* Close button */}
          {!spinning && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M1 13L13 1" stroke="#666" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}

          {/* Header */}
          <div className="text-center mb-6">
            <motion.div
              animate={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
              className="text-4xl mb-2"
            >
              🎰
            </motion.div>
            <h2 className="text-2xl font-bold text-[#174540]">Roue de la Chance</h2>
            <p className="text-gray-500 text-sm mt-1">Tentez votre chance et gagnez des réductions !</p>
          </div>

          {/* Wheel */}
          <div className="relative flex items-center justify-center mb-6">
            {/* Pointer / Arrow */}
            <div className="absolute -top-1 z-20 flex flex-col items-center">
              <div className="w-0 h-0 border-l-[14px] border-r-[14px] border-t-[24px] border-l-transparent border-r-transparent border-t-[#174540] drop-shadow-lg" />
            </div>

            {/* Outer glow ring */}
            <div className="absolute w-[310px] h-[310px] rounded-full"
              style={{
                background: 'conic-gradient(from 0deg, #FF6B6B, #FFD93D, #6BCB77, #4ECDC4, #45B7D1, #FF6B6B)',
                filter: 'blur(8px)',
                opacity: spinning ? 0.6 : 0.3,
                transition: 'opacity 0.5s'
              }}
            />

            {/* Wheel container */}
            <motion.div
              animate={{ rotate: rotation }}
              transition={{ duration: 4, ease: [0.17, 0.67, 0.12, 0.99] }}
              className="relative"
            >
              <canvas
                ref={canvasRef}
                width={280}
                height={280}
                className="rounded-full shadow-xl cursor-pointer"
                onClick={handleSpin}
              />
            </motion.div>
          </div>

          {/* Spin button */}
          {!showResult && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSpin}
              disabled={spinning}
              className={`w-full py-3 rounded-xl font-bold text-white text-lg transition-all ${
                spinning
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#174540] to-[#2a7a6f] hover:shadow-lg'
              }`}
            >
              {spinning ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    ⏳
                  </motion.span>
                  La roue tourne...
                </span>
              ) : canSpin ? (
                'Tourner la roue 🎯'
              ) : (
                'Déjà joué aujourd\'hui'
              )}
            </motion.button>
          )}

          {/* Result Modal */}
          <AnimatePresence>
            {showResult && result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className={`mt-2 p-5 rounded-2xl text-center ${
                  result.hasWon
                    ? 'bg-gradient-to-br from-yellow-50 to-green-50 border-2 border-green-200'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <div className="text-3xl mb-2">{result.hasWon ? '🎉' : '😊'}</div>
                <p className={`font-bold text-lg ${result.hasWon ? 'text-green-700' : 'text-gray-700'}`}>
                  {result.message}
                </p>
                {result.hasWon && result.couponCode && (
                  <div className="mt-3 bg-white rounded-xl p-3 border-2 border-dashed border-green-300">
                    <p className="text-xs text-gray-500 mb-1">Votre code promo</p>
                    <p className="text-xl font-mono font-bold text-[#174540] tracking-wider">{result.couponCode}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Expire le {new Date(result.expirationDate).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="mt-4 px-6 py-2 bg-[#174540] text-white rounded-lg font-medium hover:bg-[#1d5952] transition-colors"
                >
                  Fermer
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RouletteWheel;
